<template>
  <div class="app-select" :class="{ 'app-select--block': block }">
    <v-menu
      ref="menu"
      v-model="menuOpen"
      offset-y
      nudge-bottom="4"
      :min-width="menuMinWidth"
      max-height="60vh"
      :disabled="disabled"
      :close-on-content-click="false"
      content-class="app-menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <app-button
          :id="id"
          kind="field"
          chevron
          :icon="icon"
          :width="width"
          :block="block"
          :disabled="disabled"
          :class="buttonClasses"
          :title="fullLabel"
          :aria-label="fullLabel"
          :aria-invalid="hasError ? 'true' : null"
          :aria-describedby="errorText ? errorId : null"
          v-bind="attrs"
          v-on="on"
          @click="onActivatorClick"
        >{{ label }}</app-button>
      </template>
      <slot name="menu" :close="close">
        <div class="app-menu__list" role="none" @keydown.tab="onTab">
          <template v-for="(item, i) in entries">
            <div
              v-if="item.divider"
              :key="'d' + i"
              class="app-menu__divider"
              role="separator"
            ></div>
            <div
              v-else-if="item.header"
              :key="'h' + i"
              class="app-menu__header"
              role="presentation"
            >{{ item.header }}</div>
            <button
              v-else
              :id="itemId(i)"
              :key="'i' + i"
              type="button"
              class="v-list-item app-menu__item"
              role="menuitemradio"
              :aria-checked="isSelected(item) ? 'true' : 'false'"
              :disabled="item.disabled"
              @click="select(item)"
              @keydown.enter.stop.prevent="select(item)"
            >
              <span class="app-menu__text">{{ item.text }}</span>
              <app-icon v-if="isSelected(item)" name="check" :size="14" class="app-menu__check" />
            </button>
          </template>
        </div>
      </slot>
    </v-menu>
    <p v-if="errorText" :id="errorId" class="app-field__error">
      <app-icon name="alert" :size="14" />{{ errorText }}
    </p>
  </div>
</template>

<script>
// Auswahl (Spezifikation 2.6): Feld-Knopf „Prefix: Wert“ mit Menü (v-menu offset-y, content-class app-menu).
// items: [{ value, text }] sowie { header: 'Verbund-Exporte' } und { divider: true }; optional { disabled: true }.
// hidePrefix (seit 12.10.2026): sichtbar nur der Wert, das Präfix steht im zugänglichen Namen und im Tooltip (Ebenen im
// Gruppierungs-Panel der Verkäufe: „Ebene 1: Fahrer“).
// Ereignisse: input (Wert), open, close. Slot menu ({ close }) ersetzt die Liste (Mehrfachauswahl, Zeitraum);
// das Menü bleibt dabei offen, bis close() aufgerufen oder daneben geklickt wird.
// Pfeiltasten/Pos1/Ende/Esc über die Tastatursteuerung von v-menu (Einträge tragen dafür die Klasse v-list-item). Seit
// 12.10.2026 (Prüfung Verkäufe, Muster Menüknopf) folgt der Fokus dem markierten Eintrag, damit Screenreader ihn samt
// Zustand ansagen; mit Enter oder Leertaste geöffnet steht er auf dem gewählten Eintrag. Enter und Leertaste wählen den
// Eintrag mit Fokus, Tab schließt und geht vom Knopf aus weiter, nach einer Wahl (auch mit der Maus) liegt der Fokus
// wieder am Knopf (vorher fiel er mit dem Menü auf die Seite). Gilt nicht für den Slot menu.
import AppButton from './AppButton.vue';
import AppIcon from './AppIcon.vue';

// Vuetify leitet die Ebene eines Menüs vom nächsten Vorfahren mit z-index ab (sonst 0 + 2). Mindestens 6 + 2 = 8,
// damit Menüs über der festen Handy-App-Leiste und Tab-Bar (z-index 5) liegen; über Dialogen bleibt es dynamisch.
export function raiseMenu(menu) {
  if (menu && typeof menu.stackMinZIndex === 'number' && menu.stackMinZIndex < 6) {
    menu.stackMinZIndex = 6;
  }
}

// Eintrag fokussieren, sobald VMenu den Inhalt zeigt (isContentActive folgt isActive erst zwei Bilder später; bis dahin
// ist der Inhalt ausgeblendet und nicht fokussierbar): einige Bilder lang erneut versuchen
function focusWhenShown(menu, tile, tries) {
  if (!menu.isActive || document.activeElement === tile) {
    return;
  }
  if (menu.isContentActive && tile.offsetParent !== null) {
    tile.focus({ preventScroll: true });
  } else if (tries > 0) {
    window.requestAnimationFrame(() => focusWhenShown(menu, tile, tries - 1));
  }
}

// Fokus folgt dem markierten Eintrag eines VMenu (listIndex, Pfeiltasten): vm beobachtet, isOpen() sagt, ob das Menü
// offen ist und der Fokus folgen soll. Die Einträge brauchen dafür @keydown.enter.stop.prevent (sonst wählte Enter
// zweimal: als Klick auf den Knopf und über VMenu).
export function followMenuFocus(vm, menu, isOpen) {
  if (!menu) {
    return;
  }
  vm.$watch(
    () => menu.listIndex,
    (index) => {
      const tile = index >= 0 ? menu.tiles[index] : null;
      if (tile && isOpen()) {
        focusWhenShown(menu, tile, 12);
      }
    }
  );
}

// im gerade geöffneten Menü einen Eintrag markieren und fokussieren (selector, sonst der erste wählbare), sobald VMenu
// den Inhalt zeigt
export function focusMenuItem(menu, selector) {
  if (!menu) {
    return;
  }
  const attempt = (tries) => {
    window.requestAnimationFrame(() => {
      if (!menu.isActive) {
        return;
      }
      if (!menu.isContentActive) {
        if (tries > 0) {
          attempt(tries - 1);
        }
        return;
      }
      menu.getTiles();
      const tiles = menu.tiles || [];
      let index = selector ? tiles.findIndex((tile) => tile.matches(selector) && tile.tabIndex > -1) : -1;
      if (index < 0) {
        index = tiles.findIndex((tile) => tile.tabIndex > -1 && !tile.disabled);
      }
      if (index < 0) {
        return;
      }
      menu.listIndex = index;
      focusWhenShown(menu, tiles[index], 12);
    });
  };
  menu.$nextTick(() => attempt(12));
}

function same(a, b) {
  return a === b || ((a === null || a === undefined) && (b === null || b === undefined));
}

export default {
  name: 'AppSelect',
  components: { AppButton, AppIcon },
  props: {
    value: { type: null, default: null },
    items: { type: Array, default: () => [] },
    prefix: { type: String, default: null },
    hidePrefix: { type: Boolean, default: false },
    placeholder: { type: String, default: null },
    icon: { type: String, default: null },
    width: { type: [Number, String], default: null },
    menuWidth: { type: [Number, String], default: 240 },
    block: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    error: { type: [String, Boolean], default: null },
    id: { type: String, default: null }
  },
  data() {
    return { menuOpen: false };
  },
  computed: {
    entries() {
      return this.items.map((item) => {
        if (item !== null && typeof item === 'object') {
          return item;
        }
        return { value: item, text: String(item) };
      });
    },
    selected() {
      return this.entries.find(item => !item.divider && !item.header && same(item.value, this.value)) || null;
    },
    isPlaceholder() {
      return !this.selected;
    },
    // Einträge dürfen einen eigenen Knopftext haben (buttonText, z. B. „Gruppierung“ für „Keine Gruppierung“) und
    // einen eigenen Text für Name und Tooltip (ariaText, z. B. „keine“)
    label() {
      const text = this.selected ? (this.selected.buttonText || this.selected.text) : (this.placeholder || '');
      return this.prefix && !this.hidePrefix ? this.prefix + ': ' + text : text;
    },
    // verborgenes Präfix: voller Text als zugänglicher Name und Tooltip
    fullLabel() {
      if (!this.hidePrefix || !this.prefix) {
        return null;
      }
      return this.prefix + ': ' + (this.selected ? (this.selected.ariaText || this.selected.text) : (this.placeholder || ''));
    },
    hasError() {
      return !!this.error;
    },
    errorText() {
      return typeof this.error === 'string' ? this.error : '';
    },
    errorId() {
      return (this.id || 'app-select-' + this._uid) + '-fehler';
    },
    buttonClasses() {
      return {
        'app-btn--error': this.hasError,
        'app-btn--placeholder': this.isPlaceholder && !this.prefix
      };
    },
    menuMinWidth() {
      // Block-Felder (Formulare): mindestens Feldbreite (Vuetify-Standard); sonst die Menübreite
      return this.block ? undefined : this.menuWidth;
    }
  },
  watch: {
    menuOpen(open) {
      // mit Enter oder Leertaste geöffnet: Fokus auf den gewählten Eintrag
      if (open && this.keyboardOpen && !this.hasMenuSlot()) {
        focusMenuItem(this.$refs.menu, '[aria-checked="true"]');
      }
      this.keyboardOpen = false;
      this.$emit(open ? 'open' : 'close');
    }
  },
  created() {
    // nicht reaktiv: zuletzt mit der Tastatur geöffnet (Klick mit detail 0)
    this.keyboardOpen = false;
  },
  mounted() {
    raiseMenu(this.$refs.menu);
    followMenuFocus(this, this.$refs.menu, () => this.menuOpen && !this.hasMenuSlot());
  },
  methods: {
    hasMenuSlot() {
      return !!(this.$scopedSlots.menu || this.$slots.menu);
    },
    itemId(index) {
      return (this.id || 'app-select-' + this._uid) + '-eintrag-' + index;
    },
    onActivatorClick(e) {
      this.keyboardOpen = !!e && e.detail === 0;
    },
    focusButton() {
      const button = this.$el ? this.$el.querySelector('.app-btn') : null;
      if (button && !button.disabled) {
        button.focus();
      }
    },
    // Tab im Menü: schließen und vom Knopf aus weiter (der Browser setzt den Fokus danach auf das nächste Element)
    onTab() {
      this.menuOpen = false;
      this.focusButton();
    },
    isSelected(item) {
      return same(item.value, this.value);
    },
    select(item) {
      if (item.disabled) {
        return;
      }
      this.menuOpen = false;
      // der gewählte Eintrag verschwindet mit dem Menü: Fokus zurück an den Knopf (Esc eines umgebenden Panels wirkt so
      // weiter)
      this.focusButton();
      if (!same(item.value, this.value)) {
        this.$emit('input', item.value);
      }
    },
    close() {
      this.menuOpen = false;
    },
    open() {
      if (!this.disabled) {
        this.menuOpen = true;
      }
    }
  }
};
</script>
